/* original source: https://tremor.so/docs/ui/table */

import type { SetOptional } from 'type-fest'
import { AnimatePresence, m } from 'motion/react'
import {
  cn,
  findLeafPrimitiveValue,
  focusRingClassName,
  tv,
  createNonNullableContext,
} from '@joyful/utils'
import type {
  ComponentProps,
  CSSProperties,
  Dispatch,
  ElementType,
  MouseEvent,
  ReactElement,
  ReactNode,
  SetStateAction,
} from 'react'
import { useState } from 'react'
import type { VariantProps } from '@joyful/utils'
import type { TooltipProps } from './Tooltip'
import { Checkbox } from './Checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu'
import { Skeleton } from './Skeleton'
import { Tabular } from './Tabular'
import { Tooltip } from './Tooltip'
import { VisuallyHidden } from './VisuallyHidden'
import { useRecentlyCopiedFeedback } from './useRecentlyCopiedFeedback'
import {
  ClipboardCheckIcon,
  ClipboardCopyIcon,
  ArrowDownIcon,
  ArrowLeftToLineIcon,
  ArrowRightToLineIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  CopyIcon,
  EyeOffIcon,
  ListFilterIcon,
  ListRestartIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type {
  Cell as TanStackCell,
  CellContext as TanStackCellContext,
  CellData,
  Column as TanStackColumn,
  ColumnDef as TanStackColumnDef,
  Header as TanStackHeader,
  HeaderGroup as TanStackHeaderGroup,
  Row as TanStackRow,
  RowData,
  Table as TanStackTable,
  TableFeatures,
  TableOptions,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  createSortedRowModel,
  flexRender,
  stockFeatures,
  Subscribe,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual'
import { useMergeRefs } from 'react-merge-refs'

export interface TableBulkRowSelection {
  index: number
  tableKey?: string
}

export interface CopyableTableValueProps extends ComponentProps<'span'> {
  children: ReactNode
  value?: string
}

function CopyableTableValue({
  children,
  className,
  value,
  ...props
}: CopyableTableValueProps) {
  const { didRecentlyCopy, isTooltipOpen, markCopied, setIsTooltipOpen } =
    useRecentlyCopiedFeedback()
  const valueToCopy =
    value ?? findLeafPrimitiveValue(children)?.toString() ?? ''

  const content = (
    <span
      className={cn(
        valueToCopy !== '' &&
          'dark:hover:bg-tremor-background-subtle dark:data-[state=open]:bg-tremor-background-subtle cursor-pointer rounded-xs transition-[background-color,box-shadow] hover:bg-gray-200 hover:shadow-[0_0_0_2px_var(--tremor-border)] data-[state=open]:bg-gray-200',
        className,
      )}
      {...props}
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        if (valueToCopy !== '') {
          void navigator.clipboard.writeText(valueToCopy).then(() => {
            markCopied()
          })
        }

        props.onClick?.(event)
      }}
    >
      {children}
    </span>
  )

  if (valueToCopy === '') {
    return content
  }

  const CopyIcon = didRecentlyCopy ? ClipboardCheckIcon : ClipboardCopyIcon
  return (
    <Tooltip
      contentProps={{
        closeAnimation: 'instant',
      }}
      tooltipRootProps={{
        onOpenChange: setIsTooltipOpen,
        open: isTooltipOpen,
      }}
      content={
        <span className="inline-flex items-center gap-1 text-xs">
          <CopyIcon className="size-3" />
          {didRecentlyCopy ? 'Copied!' : `Copy to clipboard`}
        </span>
      }
    >
      {content}
    </Tooltip>
  )
}

export function TableRoot({
  children,
  className,
  ref,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div ref={ref}>
      <div
        className={cn('w-full overflow-auto whitespace-nowrap', className)}
        {...props}
      >
        {children}
      </div>
    </div>
  )
}

type TableRowVariants = VariantProps<typeof tableRowVariants>

type TableBorderVariant = 'horizontal' | 'none'
type TableCellVariant = 'display' | 'form'

interface OverridableTableVariantsContextValue {
  borderVariant?: TableBorderVariant
  cellVariant?: TableCellVariant
  density?: NonNullable<TableRowVariants['density']>
  rowColorVariant?: NonNullable<TableRowVariants['rowColorVariant']>
}

export type TableProps = ComponentProps<'table'> &
  OverridableTableVariantsContextValue

const TableVariantsContext = createNonNullableContext<
  Required<OverridableTableVariantsContextValue>
>('TableVariantsContext')

export function Table({
  borderVariant = 'horizontal',
  cellVariant = 'display',
  className,
  density = 'default',
  rowColorVariant = 'none',
  ...props
}: TableProps) {
  return (
    <TableVariantsContext.Context
      value={{
        borderVariant,
        cellVariant,
        density,
        rowColorVariant,
      }}
    >
      <table
        className={cn(
          // base
          'h-px w-full caption-bottom overflow-x-auto',
          // border color
          'border-tremor-border',
          className,
        )}
        {...props}
      />
    </TableVariantsContext.Context>
  )
}

const tableHeadVariants = tv({
  base: null,
  defaultVariants: {
    stickiness: 'default',
  },
  variants: {
    stickiness: {
      default: null,
      sticky: 'sticky top-0 z-10',
    },
  },
})

export type TableHeadProps = ComponentProps<'thead'> &
  VariantProps<typeof tableHeadVariants>

export function TableHead({ className, stickiness, ...props }: TableHeadProps) {
  return (
    <thead
      className={cn(tableHeadVariants({ stickiness }), className)}
      {...props}
    />
  )
}

const tableHeaderCellVariants = tv({
  base: [
    'border-b text-left text-sm font-semibold whitespace-nowrap',
    'text-tremor-content',
    'border-tremor-border',
    'has-data-header-menu:hover:bg-tremor-background-subtle has-[[data-header-menu][data-state=open]]:bg-tremor-background-subtle',
    focusRingClassName,
  ],

  defaultVariants: {
    align: 'start',
    textVariant: 'default',
  },

  slots: {
    content: [
      'flex gap-1.5 text-start text-xs font-medium whitespace-nowrap',
      focusRingClassName,
    ],
    label: null,
  },

  variants: {
    align: {
      center: {
        content: 'w-full justify-center text-center',
      },
      end: {
        content: 'w-full justify-end text-end',
      },
      start: {
        content: 'justify-start text-start',
      },
    },

    density: {
      compact: {
        content: 'px-2 py-1.5 truncate',
      },
      default: {
        content: 'px-4 py-3.5',
      },
      tight: {
        content: 'px-0.5 py-1 whitespace-normal',
      },
    },

    isRequired: {
      true: {
        content: 'block after:ml-1 after:text-red-500 after:content-["*"]',
      },
    },

    textVariant: {
      default: null,
      uppercase: {
        content: 'uppercase',
      },
    },

    wrap: {
      true: {
        base: 'align-top whitespace-normal',
        content: 'min-w-0 items-start overflow-visible whitespace-normal',
        label: 'min-w-0 flex-1 wrap-break-word whitespace-normal',
      },
    },
  },
})

export interface TableHeaderCellProps<TData extends RowData = RowData>
  extends
    Omit<ComponentProps<'th'>, 'align'>,
    VariantProps<typeof tableHeaderCellVariants> {
  contentClassName?: string
  header?: TableColumnHeader<TData, unknown>
  /**
   * Absolutely positioned drag handle rendered on the cell's trailing edge.
   */
  resizeHandle?: ReactNode
  /**
   * @default false
   */
  shouldHideLabel?: boolean
  readonly tooltip?: string | Omit<TooltipProps, 'children'>
}

function selectColumnSortAndPin(
  state: {
    columnPinning: { end?: Array<string>; start?: Array<string> }
    sorting: Array<{ desc: boolean; id: string }>
  },
  columnId: string,
) {
  const sort = state.sorting.find((item) => {
    return item.id === columnId
  })
  const isPinnedStart = (state.columnPinning.start ?? []).includes(columnId)
  const isPinnedEnd = (state.columnPinning.end ?? []).includes(columnId)
  return {
    pinnedSide: isPinnedStart ? 'start' : isPinnedEnd ? 'end' : false,
    sortDirection: sort === undefined ? false : sort.desc ? 'desc' : 'asc',
  } as const
}

function selectDataTableRowView(
  state: {
    columnOrder: Array<string>
    columnPinning: { end?: Array<string>; start?: Array<string> }
    columnVisibility: Record<string, boolean>
    rowSelection: Record<string, boolean>
  },
  rowId: string,
) {
  return {
    columnOrder: state.columnOrder,
    columnPinning: state.columnPinning,
    columnVisibility: state.columnVisibility,
    isSelected: state.rowSelection[rowId] ?? false,
  }
}

export function TableHeaderCell<TData extends RowData = RowData>({
  align,
  children,
  className,
  contentClassName,
  density,
  header,
  isRequired,
  resizeHandle,
  shouldHideLabel = false,
  textVariant,
  tooltip,
  wrap,
  ...props
}: TableHeaderCellProps<TData>) {
  const tableVariants = TableVariantsContext.useRequiredContext()
  const classNames = tableHeaderCellVariants({
    align,
    density: density ?? tableVariants.density,
    isRequired,
    textVariant,
    wrap,
  })

  const hasColumnMenu =
    header !== undefined &&
    header.column.id !== SELECTION_COLUMN_ID &&
    (header.column.getCanSort() ||
      header.column.getCanPin() ||
      header.column.getCanHide() ||
      header.column.columnDef.meta?.headerMenuFilter !== undefined)

  const label = shouldHideLabel ? (
    <VisuallyHidden>{children}</VisuallyHidden>
  ) : wrap ? (
    <div className={classNames.label()}>{children}</div>
  ) : (
    children
  )

  const headerCellClassName = cn(
    classNames.base(),
    (hasColumnMenu || resizeHandle !== undefined) && 'relative',
    className,
  )
  const headerCellContent = (
    <>
      <div className={cn(classNames.content(), contentClassName)}>
        {hasColumnMenu && header !== undefined ? (
          <ColumnHeaderMenu header={header}>{label}</ColumnHeaderMenu>
        ) : (
          label
        )}
      </div>
      {resizeHandle}
    </>
  )

  const content =
    header === undefined ? (
      <th className={headerCellClassName} tabIndex={-1} scope="col" {...props}>
        {headerCellContent}
      </th>
    ) : (
      <Subscribe
        source={header.table.store}
        selector={(state) => {
          return selectColumnSortAndPin(state, header.column.id)
        }}
      >
        {(headerView) => {
          const ariaSort = header.column.getCanSort()
            ? // oxlint-disable-next-line joyful/prefer-boolean-check -- sortDirection is false | 'asc' | 'desc'
              headerView.sortDirection === false
              ? 'none'
              : headerView.sortDirection === 'asc'
                ? 'ascending'
                : 'descending'
            : undefined
          return (
            <th
              className={headerCellClassName}
              aria-sort={ariaSort}
              tabIndex={-1}
              scope="col"
              {...props}
            >
              {headerCellContent}
            </th>
          )
        }}
      </Subscribe>
    )

  if (tooltip === undefined) {
    return content
  }

  const tooltipProps =
    typeof tooltip === 'string'
      ? ({
          content: tooltip,
          tooltipRootProps: {
            delayDuration: 500,
          },
        } satisfies Omit<TooltipProps, 'children'>)
      : tooltip
  return (
    <Tooltip
      {...tooltipProps}
      tooltipRootProps={{
        delayDuration: 500,
        ...tooltipProps?.tooltipRootProps,
      }}
      contentProps={tooltipProps.contentProps}
    >
      {content}
    </Tooltip>
  )
}

const activeMenuCheck = <CheckIcon className="size-4" />

export interface ColumnHeaderMenuFilterControls {
  close: () => void
  goBack: () => void
}

export interface ColumnHeaderMenuFilter {
  badge?: ReactElement<unknown>
  renderPanel: (controls: ColumnHeaderMenuFilterControls) => ReactNode
}

type ColumnHeaderMenuView = 'menu' | 'filter' | null

function ColumnHeaderMenu<TData extends RowData>({
  children,
  header,
}: {
  children: ReactNode
  header: TableColumnHeader<TData, unknown>
}) {
  const { column } = header

  const [view, setView] = useState<ColumnHeaderMenuView>(null)

  const headerMenuFilter = column.columnDef.meta?.headerMenuFilter
  const canSort = column.getCanSort()
  const canPin = column.getCanPin()
  const canHide = column.getCanHide()
  const headerLabel =
    typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : (column.columnDef.meta?.headerLabel ?? '')
  const hasHeaderLabel = headerLabel.trim().length > 0
  const hasMenuActions = canSort || canPin || canHide || hasHeaderLabel

  return (
    <Subscribe
      source={header.table.store}
      selector={(state) => {
        return selectColumnSortAndPin(state, column.id)
      }}
    >
      {(headerView) => {
        const pinnedSide = headerView.pinnedSide
        const sortDirection = headerView.sortDirection
        const sortIndicator =
          sortDirection === 'asc' ? (
            <ArrowUpIcon className="text-tremor-content-strong size-3.5 shrink-0" />
          ) : sortDirection === 'desc' ? (
            <ArrowDownIcon className="text-tremor-content-strong size-3.5 shrink-0" />
          ) : canSort ? (
            <ChevronsUpDownIcon className="text-tremor-content-subtle size-3.5 shrink-0" />
          ) : null

        return (
          <>
            {children}
            {sortIndicator}

            <DropdownMenu
              modal={false}
              open={view !== null}
              onOpenChange={(open) => {
                setView(open ? 'menu' : null)
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-header-menu=""
                  aria-label={
                    headerLabel.length > 0
                      ? `${headerLabel} column options`
                      : 'Column options'
                  }
                  className={cn(
                    'absolute inset-0 cursor-pointer rounded-sm',
                    focusRingClassName,
                  )}
                />
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="start"
                className={cn(
                  view === 'filter' ? 'max-w-md min-w-72' : 'min-w-48',
                )}
                padding={view === 'filter' ? 'none' : undefined}
              >
                {view === 'filter' ? (
                  <div
                    role="presentation"
                    onKeyDown={(event) => {
                      if (event.key !== 'Escape') {
                        event.stopPropagation()
                      }
                    }}
                  >
                    {headerMenuFilter?.renderPanel({
                      close: () => {
                        setView(null)
                      },
                      goBack: () => {
                        setView('menu')
                      },
                    })}
                  </div>
                ) : (
                  <>
                    {hasHeaderLabel ? (
                      <>
                        <DropdownMenuLabel className="px-2 py-1">
                          {headerLabel}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                      </>
                    ) : null}

                    {headerMenuFilter !== undefined ? (
                      <>
                        <DropdownMenuItem
                          icon={<ListFilterIcon />}
                          endAdornment={headerMenuFilter.badge}
                          onSelect={() => {
                            setView('filter')
                          }}
                        >
                          {`Filter`}
                        </DropdownMenuItem>

                        {hasMenuActions ? <DropdownMenuSeparator /> : null}
                      </>
                    ) : null}

                    {canSort ? (
                      <>
                        <DropdownMenuItem
                          icon={<ArrowUpIcon />}
                          endAdornment={
                            sortDirection === 'asc'
                              ? activeMenuCheck
                              : undefined
                          }
                          shouldCloseOnSelect
                          onSelect={() => {
                            column.toggleSorting(false)
                          }}
                        >
                          {`Sort ascending`}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          icon={<ArrowDownIcon />}
                          endAdornment={
                            sortDirection === 'desc'
                              ? activeMenuCheck
                              : undefined
                          }
                          shouldCloseOnSelect
                          onSelect={() => {
                            column.toggleSorting(true)
                          }}
                        >
                          {`Sort descending`}
                        </DropdownMenuItem>

                        {/* oxlint-disable-next-line joyful/prefer-boolean-check -- sortDirection is false | 'asc' | 'desc' */}
                        {sortDirection !== false ? (
                          <DropdownMenuItem
                            icon={<ListRestartIcon />}
                            shouldCloseOnSelect
                            onSelect={() => {
                              column.clearSorting()
                            }}
                          >
                            {`Reset sort`}
                          </DropdownMenuItem>
                        ) : null}
                      </>
                    ) : null}

                    {canPin ? (
                      <>
                        {canSort ? <DropdownMenuSeparator /> : null}

                        <DropdownMenuItem
                          icon={<ArrowLeftToLineIcon />}
                          endAdornment={
                            pinnedSide === 'start' ? activeMenuCheck : undefined
                          }
                          shouldCloseOnSelect
                          onSelect={() => {
                            column.pin(pinnedSide === 'start' ? false : 'start')
                          }}
                        >
                          {`Pin left`}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          icon={<ArrowRightToLineIcon />}
                          endAdornment={
                            pinnedSide === 'end' ? activeMenuCheck : undefined
                          }
                          shouldCloseOnSelect
                          onSelect={() => {
                            column.pin(pinnedSide === 'end' ? false : 'end')
                          }}
                        >
                          {`Pin right`}
                        </DropdownMenuItem>
                      </>
                    ) : null}

                    {canHide ? (
                      <>
                        {canSort || canPin ? <DropdownMenuSeparator /> : null}

                        <DropdownMenuItem
                          icon={<EyeOffIcon />}
                          shouldCloseOnSelect
                          onSelect={() => {
                            column.toggleVisibility(false)
                          }}
                        >
                          {`Hide column`}
                        </DropdownMenuItem>
                      </>
                    ) : null}

                    {hasHeaderLabel ? (
                      <>
                        {canSort || canPin || canHide ? (
                          <DropdownMenuSeparator />
                        ) : null}

                        <DropdownMenuItem
                          icon={<CopyIcon />}
                          disabled={headerLabel.length === 0}
                          shouldCloseOnSelect
                          onSelect={() => {
                            void navigator.clipboard.writeText(headerLabel)
                          }}
                        >
                          {`Copy to clipboard`}
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )
      }}
    </Subscribe>
  )
}

type TableBodyProps = ComponentProps<'tbody'> &
  Pick<TableProps, 'borderVariant'>

export function TableBody({
  borderVariant: borderVariantProp,
  className,
  ...props
}: TableBodyProps) {
  const tableVariants = TableVariantsContext.useRequiredContext()
  const borderVariant = borderVariantProp ?? tableVariants.borderVariant
  return (
    <tbody
      className={cn(
        borderVariant === 'horizontal' && 'divide-tremor-border divide-y',
        className,
      )}
      {...props}
    />
  )
}

const tableRowVariants = tv({
  base: 'group/row',

  compoundVariants: [
    {
      className: 'odd:bg-tremor-background even:bg-tremor-background-muted',
      isSelected: false,
      rowColorVariant: 'striped',
    },
    {
      className: 'bg-tremor-background-muted',
      isSelected: false,
      rowColorVariant: 'muted',
    },
    {
      className:
        'bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-950/30',
      isSelected: false,
      rowColorVariant: 'error',
    },
    {
      // The opaque background keeps content beneath from showing through
      // while the row slides during animated reorders.
      className:
        'bg-tremor-background font-medium [&>td:first-child]:shadow-[inset_4px_0_0_0_var(--tremor-brand)]',
      isSelected: false,
      rowColorVariant: 'attention',
    },
    {
      className: 'hover:bg-tremor-background-subtle',
      isInteractive: true,
      isSelected: false,
    },
    {
      className: 'bg-tremor-background-subtle',
      isSelected: false,
      rowColorVariant: 'current',
    },
    {
      className:
        'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/40',
      isSelected: false,
      rowColorVariant: 'success',
    },
    {
      className:
        'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-950/20 dark:hover:bg-yellow-950/30',
      isSelected: false,
      rowColorVariant: 'warning',
    },
  ],

  defaultVariants: {
    density: 'default',
    isInteractive: false,
    isSelected: false,
    rowColorVariant: 'none',
  },

  variants: {
    density: {
      compact: [
        '[&_td:last-child]:pr-1 [&_th:last-child]:pr-1',
        '[&_td:first-child]:pl-1 [&_th:first-child]:pl-1',
      ],
      default: [
        '[&_td:last-child]:pr-4 [&_th:last-child]:pr-4',
        '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4',
      ],
      tight: null,
    },

    isInteractive: {
      true: 'cursor-pointer',
    },

    isSelected: {
      // `!` wins outright over the interactive-row `hover:bg-tremor-background-subtle`
      // compound class below — same-specificity Tailwind utilities otherwise race,
      // which showed up as a stuck gray hover background on a just-selected row.
      true: 'bg-tremor-background-selected!',
    },

    rowColorVariant: {
      attention: null,
      current: null,
      error: null,
      muted: null,
      none: null,
      striped: null,
      success: null,
      warning: null,
    },
  },
})

export type TableRowProps = ComponentProps<'tr'> &
  VariantProps<typeof tableRowVariants> &
  OverridableTableVariantsContextValue & {
    as?: ElementType
  }

export function TableRow({
  as: Component = 'tr',
  borderVariant,
  cellVariant,
  className,
  density,
  isInteractive,
  isSelected,
  rowColorVariant: rowColorVariantProp,
  ...props
}: TableRowProps) {
  const tableVariants = TableVariantsContext.useRequiredContext()
  const rowColorVariant = rowColorVariantProp ?? tableVariants.rowColorVariant

  return (
    <TableVariantsContext.Context
      value={{
        borderVariant: borderVariant ?? tableVariants.borderVariant,
        cellVariant: cellVariant ?? tableVariants.cellVariant,
        density: density ?? tableVariants.density,
        rowColorVariant,
      }}
    >
      <Component
        className={cn(
          tableRowVariants({
            density: density ?? tableVariants.density,
            isInteractive: isInteractive ?? props.onClick !== undefined,
            isSelected,
            rowColorVariant,
          }),
          className,
        )}
        {...props}
      />
    </TableVariantsContext.Context>
  )
}

export const tableCellBaseStyles = {
  base: 'border-tremor-border bg-tremor-background min-w-12 border text-left align-top text-sm',
  header:
    'border-tremor-border bg-tremor-background-subtle min-w-12 border text-left align-top text-sm font-semibold',
  selected: 'bg-tremor-background-muted',
} as const

const tableCellVariants = tv({
  base: [
    'flex size-full items-center whitespace-nowrap text-tremor-content-emphasis',
    focusRingClassName,
  ],

  compoundVariants: [
    {
      cellVariant: 'display',
      className: 'p-4',
      density: 'default',
    },
    {
      cellVariant: 'display',
      className: 'py-1 px-2',
      density: 'compact',
    },
    {
      cellVariant: 'display',
      className: 'p-0',
      density: 'tight',
    },
  ],

  defaultVariants: {
    align: 'start',
  },

  variants: {
    align: {
      center: 'justify-center text-center',
      end: 'justify-end text-end',
      start: 'justify-start text-start',
    },

    cellVariant: {
      display: null,
      form: 'p-0',
      panel: 'block h-auto w-full min-w-0 items-stretch whitespace-normal p-0',
    },

    density: {
      compact: 'text-xs',
      default: 'text-sm',
      tight: 'text-xs',
    },
  },
})

export interface TableCellProps
  extends
    Omit<ComponentProps<'td'>, 'align'>,
    VariantProps<typeof tableCellVariants> {
  baseContainerClassName?: string

  /**
   * @default false
   */
  canCopyToClipboard?: boolean
  href?: string
  /**
   * Opens `href` in a new tab, for tables shown over a page the reader should keep.
   */
  hrefTarget?: '_blank'
  /**
   * Content has its own controls, so `href` sits behind it and covers only the cell's empty space.
   */
  isInteractive?: boolean
}

export function TableCell({
  align,
  baseContainerClassName,
  canCopyToClipboard = false,
  cellVariant,
  children,
  className,
  density,
  href,
  hrefTarget,
  isInteractive = false,
  ...props
}: TableCellProps) {
  const tableVariants = TableVariantsContext.useRequiredContext()
  const resolvedCellVariant = cellVariant ?? tableVariants.cellVariant
  const containerClassName = cn(
    tableCellVariants({
      align,
      cellVariant: resolvedCellVariant,
      density: density ?? tableVariants.density,
    }),
    className,
  )
  const rootClassName = cn(
    resolvedCellVariant === 'panel' && 'max-w-0 min-w-0',
    baseContainerClassName,
  )

  if (href === undefined) {
    return (
      <td className={rootClassName} {...props}>
        <div className={containerClassName}>
          {canCopyToClipboard ? (
            <CopyableTableValue>{children ?? null}</CopyableTableValue>
          ) : (
            children
          )}
        </div>
      </td>
    )
  }

  if (isInteractive) {
    return (
      <td className={cn('relative', rootClassName)} {...props}>
        <Link
          aria-hidden
          className="absolute inset-0"
          rel={hrefTarget ? 'noopener' : undefined}
          tabIndex={-1}
          target={hrefTarget}
          to={href}
        />
        <div
          className={cn(
            'pointer-events-none relative *:pointer-events-auto',
            containerClassName,
          )}
        >
          {children}
        </div>
      </td>
    )
  }

  return (
    <td className={rootClassName} {...props}>
      <Link
        className={containerClassName}
        rel={hrefTarget ? 'noopener' : undefined}
        target={hrefTarget}
        to={href}
      >
        {children}
      </Link>
    </td>
  )
}

export interface TableSkeletonProps extends Omit<TableProps, 'children'> {
  numColumns: number
  numRows: number
  /**
   * @default false
   */
  shouldHideHeader?: boolean
}

export function TableSkeleton({
  className,
  numColumns,
  numRows,
  shouldHideHeader = false,
  ...props
}: TableSkeletonProps) {
  const rows = Array.from(Array(numRows).keys())
  const cols = Array.from(Array(numColumns).keys())

  return (
    <Table className={cn('border-none', className)} {...props}>
      {shouldHideHeader ? null : (
        <TableHead>
          <TableRow
            rowColorVariant="muted"
            className={cn(
              'border-b',
              props.density === 'compact' ? 'h-6' : 'h-11',
            )}
          >
            {cols.map((col) => {
              return <th key={col} />
            })}
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {rows.map((row) => {
          return (
            <TableRow key={row}>
              {cols.map((col) => {
                return (
                  <TableCell
                    key={col}
                    className={props.density === 'compact' ? 'h-7' : 'h-14'}
                  >
                    <Skeleton className="size-full" />
                  </TableCell>
                )
              })}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export const defaultDataTableFeatures = tableFeatures({
  ...stockFeatures,
})

export const clientSortedDataTableFeatures = tableFeatures({
  ...stockFeatures,
  sortedRowModel: createSortedRowModel(),
})

export type DefaultDataTableFeatures = typeof defaultDataTableFeatures

export type ColumnDef<
  TRowData extends RowData = RowData,
  TValue extends CellData = CellData,
> = TanStackColumnDef<DefaultDataTableFeatures, TRowData, TValue>

export type Row<TRowData extends RowData = RowData> = TanStackRow<
  DefaultDataTableFeatures,
  TRowData
>

export type Cell<
  TRowData extends RowData = RowData,
  TValue extends CellData = CellData,
> = TanStackCell<DefaultDataTableFeatures, TRowData, TValue>

export type CellContext<
  TRowData extends RowData = RowData,
  TValue extends CellData = CellData,
> = TanStackCellContext<DefaultDataTableFeatures, TRowData, TValue>

export type Column<
  TRowData extends RowData = RowData,
  TValue extends CellData = CellData,
> = TanStackColumn<DefaultDataTableFeatures, TRowData, TValue>

export type TableColumnHeader<
  TRowData extends RowData = RowData,
  TValue extends CellData = CellData,
> = TanStackHeader<DefaultDataTableFeatures, TRowData, TValue>

export type HeaderGroup<TRowData extends RowData = RowData> =
  TanStackHeaderGroup<DefaultDataTableFeatures, TRowData>

export type Table<TRowData extends RowData = RowData> = TanStackTable<
  DefaultDataTableFeatures,
  TRowData
>

export type {
  CellData,
  ColumnOrderState,
  ColumnPinningPosition,
  ColumnPinningState,
  ColumnVisibilityState,
  Getter,
  HeaderContext,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
  RowData,
  ColumnDefTemplate,
  FlexRenderProps,
  Renderable,
} from '@tanstack/react-table'

declare module '@tanstack/react-table' {
  // oxlint-disable-next-line no-unused-vars
  interface TableMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
  > {
    density?: NonNullable<TableRowVariants['density']>
    lastRowSelection?: TableBulkRowSelection | null
    setLastRowSelection?: Dispatch<SetStateAction<TableBulkRowSelection | null>>

    /**
     * unique identifier for the table. include if you expect to bulk select rows across multiple tables
     */
    tableKey?: string
  }

  // oxlint-disable-next-line no-unused-vars
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    cellProps?:
      | Partial<Omit<TableCellProps, 'children'>>
      | ((
          cell: TanStackCell<TFeatures, TData, TValue>,
        ) => Partial<Omit<TableCellProps, 'children'>>)
    defaultHidden?: boolean
    defaultPin?: 'start' | 'end'
    footerCellProps?:
      | Partial<Omit<TableCellProps, 'children'>>
      | ((
          context: TanStackHeader<TFeatures, TData, TValue>,
        ) => Partial<Omit<TableCellProps, 'children'>>)
    headerCellProps?: Partial<
      Omit<TableHeaderCellProps<TData>, 'children' | 'header'>
    >

    /**
     * Plain-text column name for the header menu and accessible labels when `header` renders a node.
     */
    headerLabel?: string

    /**
     * Opt-in filter affordance rendered at the top of the column header menu.
     */
    headerMenuFilter?: ColumnHeaderMenuFilter

    /**
     * The column renders its own controls; the row link covers only the cell's empty space.
     */
    isInteractive?: boolean
  }
}

export interface DataTableProps<TRowData extends RowData>
  extends
    Omit<
      SetOptional<TableOptions<DefaultDataTableFeatures, TRowData>, 'features'>,
      'cellProps' | 'rowProps'
    >,
    Pick<
      TableProps,
      | 'borderVariant'
      | 'className'
      | 'cellVariant'
      | 'density'
      | 'style'
      | 'rowColorVariant'
    > {
  autoScrollToRowIndex?: number
  bodyProps?: Omit<TableBodyProps, 'children'>
  cellProps?:
    | Omit<TableCellProps, 'children'>
    | ((cell: Cell<TRowData>) => Omit<TableCellProps, 'children'>)
  footerRowProps?: Omit<TableRowProps, 'children'>
  headerCellProps?: Omit<TableHeaderCellProps<TRowData>, 'children' | 'header'>
  headerRowProps?: Omit<TableRowProps, 'children'>
  rowProps?:
    | Omit<TableRowProps, 'children'>
    | ((row: Row<TRowData>) => Omit<TableRowProps, 'children'>)
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>
  rowWrapper?: (props: {
    children: React.ReactElement
    row: Row<TRowData>
  }) => React.ReactElement
  /**
   * @default false
   */
  shouldAnimateRows?: boolean
  /**
   * @default false
   */
  shouldHideHeader?: boolean

  /**
   * @default false
   */
  shouldRenderFooter?: boolean
  tableHeadStickiness?: TableHeadProps['stickiness']
}

export function buildPinnedColumn<TRowData extends RowData>(
  column: Column<TRowData>,
): {
  className?: string
  containerClassName?: string
  style: CSSProperties
} {
  const isPinned = column.getIsPinned()
  const isStart = isPinned === 'start'
  const isEnd = isPinned === 'end'

  return {
    className: isPinned
      ? cn(
          isEnd &&
            column.getIsFirstColumn('end') &&
            "before:from-tremor-background-subtle before:pointer-events-none before:absolute before:inset-y-0 before:-left-[var(--right-fade-width,1.5rem)] before:z-1 before:w-[var(--right-fade-width,1.5rem)] before:bg-gradient-to-l before:to-transparent before:transition-[left,width,opacity] before:duration-300 before:content-['']",
          isStart &&
            column.getIsLastColumn('start') &&
            "after:from-tremor-background-subtle after:pointer-events-none after:absolute after:inset-y-0 after:-right-[var(--left-fade-width,1.5rem)] after:z-1 after:w-[var(--left-fade-width,1.5rem)] after:bg-gradient-to-r after:to-transparent after:transition-[right,width,opacity] after:duration-300 after:content-['']",
        )
      : undefined,
    containerClassName: cn(isPinned && 'bg-[var(--cell-bg,inherit)]'),
    style: {
      left: isStart ? column.getStart('start') : undefined,
      minWidth: isPinned ? column.columnDef.minSize : undefined,
      position: isPinned ? 'sticky' : 'relative',
      right: isEnd ? column.getAfter('end') : undefined,
      zIndex: isPinned ? 1 : 0,
    },
  }
}

export function DataTable<TRowData extends RowData>({
  autoScrollToRowIndex,
  bodyProps,
  borderVariant,
  cellProps,
  cellVariant,
  className,
  columns,
  data,
  density,
  features = defaultDataTableFeatures,
  footerRowProps,
  headerCellProps,
  headerRowProps,
  rowColorVariant,
  rowProps,
  rowVirtualizer,
  rowWrapper: RowWrapper,
  shouldAnimateRows = false,
  shouldHideHeader = false,
  shouldRenderFooter = false,
  style,
  tableHeadStickiness,
  ...tableOptions
}: DataTableProps<TRowData>) {
  const [lastRowSelection, setLastRowSelection] =
    useState<TableBulkRowSelection | null>(null)
  const table = useTable<DefaultDataTableFeatures, TRowData>({
    features,
    ...tableOptions,
    columns,
    data,
    defaultColumn: {
      enableHiding: false,
      enablePinning: false,
      ...tableOptions.defaultColumn,
    },
    meta: {
      density,
      lastRowSelection,
      setLastRowSelection,
      ...tableOptions.meta,
    },
  })

  const rows = table.getRowModel().rows
  const shouldAnimateTableRows =
    shouldAnimateRows && rowVirtualizer === undefined
  // Tables are border-collapsed by default, which paints row dividers as part
  // of the table border grid: they neither fade with row opacity nor move
  // with row transforms. Animated tables switch to separate borders and draw
  // the dividers on each row's own cells so they animate with the row.
  const shouldUseAnimatedRowDividers =
    shouldAnimateTableRows && (borderVariant ?? 'horizontal') === 'horizontal'
  const renderedRows =
    rowVirtualizer === undefined
      ? rows.map((row, index) => {
          const finalRowProps =
            typeof rowProps === 'function' ? rowProps(row) : rowProps
          const tableRow = (
            <DataTableRow
              key={row.id}
              {...finalRowProps}
              row={row}
              cellProps={cellProps}
              shouldAnimateRows={shouldAnimateRows}
              ref={
                index === autoScrollToRowIndex
                  ? (node) => {
                      node?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      })
                    }
                  : undefined
              }
            />
          )

          if (RowWrapper) {
            return (
              <RowWrapper key={row.id} row={row}>
                {tableRow}
              </RowWrapper>
            )
          }

          return tableRow
        })
      : rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows.at(virtualRow.index)
          if (row === undefined) {
            return null
          }

          const finalRowProps =
            typeof rowProps === 'function' ? rowProps(row) : rowProps
          const tableRow = (
            <DataTableRow
              key={row.id}
              {...finalRowProps}
              row={row}
              cellProps={cellProps}
              rowVirtualizer={rowVirtualizer}
              virtualRow={virtualRow}
              ref={
                virtualRow.index === autoScrollToRowIndex
                  ? () => {
                      rowVirtualizer.scrollToIndex(autoScrollToRowIndex, {
                        align: 'center',
                        behavior: 'smooth',
                      })
                    }
                  : undefined
              }
            />
          )

          if (RowWrapper) {
            return (
              <RowWrapper key={row.id} row={row}>
                {tableRow}
              </RowWrapper>
            )
          }

          return tableRow
        })

  return (
    <Table
      borderVariant={borderVariant}
      className={cn(
        shouldAnimateTableRows && 'border-separate border-spacing-0',
        className,
      )}
      cellVariant={cellVariant}
      rowColorVariant={rowColorVariant}
      density={density}
      style={style}
    >
      {shouldHideHeader ? null : (
        <TableHead stickiness={tableHeadStickiness}>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableRow
                key={headerGroup.id}
                cellVariant="display"
                density={density === 'tight' ? 'compact' : density}
                rowColorVariant="muted"
                {...headerRowProps}
              >
                {headerGroup.headers.map((header) => {
                  const pinnedColumn = buildPinnedColumn(header.column)
                  const finalHeaderCellProps = {
                    ...headerCellProps,
                    ...header.column.columnDef.meta?.headerCellProps,
                  }

                  return (
                    <TableHeaderCell
                      key={header.id}
                      header={header}
                      {...finalHeaderCellProps}
                      style={{
                        ...finalHeaderCellProps.style,
                        ...pinnedColumn.style,
                      }}
                      className={cn(
                        finalHeaderCellProps.className,
                        pinnedColumn.className,
                        pinnedColumn.containerClassName,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHeaderCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableHead>
      )}

      <TableBody
        borderVariant={shouldUseAnimatedRowDividers ? 'none' : undefined}
        style={
          rowVirtualizer === undefined
            ? undefined
            : {
                height: rowVirtualizer.getTotalSize(),
                position: 'relative',
              }
        }
        {...bodyProps}
        className={cn(
          shouldUseAnimatedRowDividers &&
            '[&>tr:not(:last-child)>td]:border-tremor-border [&>tr:not(:last-child)>td]:border-b',
          bodyProps?.className,
        )}
      >
        {shouldAnimateTableRows ? (
          <AnimatePresence initial={false}>{renderedRows}</AnimatePresence>
        ) : (
          renderedRows
        )}

        {shouldRenderFooter
          ? table.getFooterGroups().map((footerGroup) => {
              return (
                <TableRow
                  key={footerGroup.id}
                  cellVariant="display"
                  density={density === 'tight' ? 'compact' : density}
                  {...footerRowProps}
                >
                  {footerGroup.headers.map((header) => {
                    const footerCellProps =
                      typeof header.column.columnDef.meta?.footerCellProps ===
                      'function'
                        ? header.column.columnDef.meta.footerCellProps(header)
                        : header.column.columnDef.meta?.footerCellProps
                    return (
                      <TableCell key={header.id} {...footerCellProps}>
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          : null}
      </TableBody>
    </Table>
  )
}

function createRowSelectionToggleHandler<TData extends RowData>(
  context: CellContext<TData, unknown>,
) {
  return (event: MouseEvent) => {
    event.stopPropagation()
    const lastRowSelection =
      context.table.options.meta?.lastRowSelection ?? null
    const tableKey = context.table.options.meta?.tableKey

    if (
      event.nativeEvent.shiftKey &&
      lastRowSelection !== null &&
      (tableKey === undefined || tableKey === lastRowSelection.tableKey)
    ) {
      const from = Math.min(lastRowSelection.index, context.row.index)
      const to = Math.max(lastRowSelection.index, context.row.index)
      const newSelection: RowSelectionState = {
        ...context.table.store.state.rowSelection,
      }
      context.table
        .getRowModel()
        .rows.slice(from, to + 1)
        .forEach((row) => {
          if (row.getCanSelect()) {
            newSelection[row.id] = true
          }
        })

      context.table.setRowSelection(newSelection)
    } else {
      context.row.toggleSelected()
      context.table.options.meta?.setLastRowSelection?.({
        index: context.row.index,
        tableKey,
      })
    }
  }
}

interface DataTableRowProps<TRowData extends RowData>
  extends
    Omit<TableRowProps, 'children'>,
    Pick<DataTableProps<TRowData>, 'cellProps'> {
  cellProps?: DataTableProps<TRowData>['cellProps']
  row: Row<TRowData>
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>
  shouldAnimateRows?: boolean
  virtualRow?: VirtualItem
}

function DataTableRow<TRowData extends RowData>({
  cellProps,
  ref,
  row,
  rowVirtualizer,
  shouldAnimateRows = false,
  virtualRow,
  ...restProps
}: DataTableRowProps<TRowData>) {
  const finalRef = useMergeRefs<HTMLTableRowElement>([
    ref,
    (node) => {
      return rowVirtualizer?.measureElement(node)
    },
  ])
  const rowStyle: CSSProperties =
    virtualRow === undefined
      ? { ...restProps.style }
      : {
          ...restProps.style,
          position: 'absolute',
          transform: `translateY(${virtualRow.start}px)`,
        }

  return (
    <Subscribe
      source={row.table.store}
      selector={(state) => {
        return selectDataTableRowView(state, row.id)
      }}
    >
      {(rowView) => {
        const children = (
          <>
            {row.getVisibleCells().map((cell) => {
              const columnDefCellProps =
                typeof cell.column.columnDef.meta?.cellProps === 'function'
                  ? cell.column.columnDef.meta.cellProps(cell)
                  : cell.column.columnDef.meta?.cellProps
              const globalCellProps =
                typeof cellProps === 'function' ? cellProps(cell) : cellProps
              const pinnedColumn = buildPinnedColumn(cell.column)
              const finalCellProps = {
                ...globalCellProps,
                ...columnDefCellProps,
              }
              return (
                <TableCell
                  key={cell.id}
                  isInteractive={cell.column.columnDef.meta?.isInteractive}
                  {...finalCellProps}
                  style={{
                    ...finalCellProps.style,
                    ...pinnedColumn.style,
                  }}
                  baseContainerClassName={cn(
                    globalCellProps?.baseContainerClassName,
                    columnDefCellProps?.baseContainerClassName,
                    pinnedColumn.containerClassName,
                  )}
                  className={cn(
                    globalCellProps?.className,
                    columnDefCellProps?.className,
                    pinnedColumn.className,
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              )
            })}
          </>
        )
        const sharedRowProps = {
          'data-index': virtualRow?.index,
          ...restProps,
          isSelected: rowView.isSelected,
          ref: finalRef,
          style: rowStyle,
        }

        if (shouldAnimateRows && virtualRow === undefined) {
          return (
            <AnimatedTableRow {...sharedRowProps}>{children}</AnimatedTableRow>
          )
        }

        return <TableRow {...sharedRowProps}>{children}</TableRow>
      }}
    </Subscribe>
  )
}

function AnimatedTableRow(props: TableRowProps) {
  const {
    onAnimationStart: _onAnimationStart,
    onDrag: _onDrag,
    onDragEnd: _onDragEnd,
    onDragStart: _onDragStart,
    ...motionSafeProps
  } = props
  const motionRowProps = {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    initial: { opacity: 0 },
    transition: animatedTableTransition,
  }

  return <TableRow as={m.tr} {...motionRowProps} {...motionSafeProps} />
}

const animatedTableTransition = {
  duration: 0.16,
  ease: 'easeInOut',
} as const

interface SelectionColumnDefinitionOptions<TData extends RowData = RowData> {
  cell?: (
    context: CellContext<TData, unknown>,
    defaultCell: ReactElement,
  ) => ReactElement
  /** Replaces the row number that the checkbox covers when `visibility` is `index`. */
  renderIndex?: (context: CellContext<TData, unknown>) => ReactElement
  /** @default 'hover' */
  visibility?: 'always' | 'hover' | 'index'
}

const selectionCheckboxRevealVariants = tv({
  variants: {
    visibility: {
      always: null,
      hover:
        'group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 md:opacity-0',
      index:
        'group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 md:opacity-0',
    },
  },
})

export const SELECTION_COLUMN_ID = 'select'

export function createSelectionColumnDefinition<
  TData extends RowData = RowData,
>({
  cell,
  renderIndex,
  visibility = 'hover',
}: SelectionColumnDefinitionOptions<TData> = {}): ColumnDef<TData, unknown> {
  const revealClassName = selectionCheckboxRevealVariants({ visibility })
  return {
    cell: (context) => {
      const isSelected = context.row.getIsSelected()
      const isCheckboxVisible =
        visibility === 'always' ||
        isSelected ||
        (visibility === 'index' &&
          (context.table.getIsSomeRowsSelected() ||
            context.table.getIsAllRowsSelected()))
      const checkbox = (
        <Checkbox
          className={cn(
            'hit-area-3 transition-opacity',
            visibility !== 'always' &&
              (isCheckboxVisible ? 'opacity-100' : revealClassName),
          )}
          checked={isSelected}
          onClick={createRowSelectionToggleHandler(context)}
          aria-label={`Select row ${context.row.index + 1}`}
        />
      )

      const defaultCell =
        visibility !== 'index' ? (
          <div className="flex items-center justify-center">{checkbox}</div>
        ) : (
          <SelectionIndexLayer isCheckboxVisible={isCheckboxVisible}>
            {renderIndex ? (
              renderIndex(context)
            ) : (
              <Tabular size="xs" tone="content" weight="semibold">
                {context.row.index + 1}
              </Tabular>
            )}
            {checkbox}
          </SelectionIndexLayer>
        )

      return cell ? cell(context, defaultCell) : defaultCell
    },
    enableHiding: false,
    enablePinning: true,
    enableSorting: false,
    header: (params) => {
      const isAllPageRowsSelected = params.table.getIsAllPageRowsSelected()
      const isSomePageRowsSelected = params.table.getIsSomePageRowsSelected()
      const isCheckboxVisible =
        visibility === 'always' ||
        (visibility === 'hover' && isSomePageRowsSelected) ||
        (visibility === 'index' && params.table.getIsSomeRowsSelected())
      const checkbox = (
        <Checkbox
          className={cn(
            'transition-opacity',
            visibility !== 'always' &&
              (isCheckboxVisible ? 'opacity-100' : revealClassName),
          )}
          checked={
            isAllPageRowsSelected ||
            (isSomePageRowsSelected &&
              !isAllPageRowsSelected &&
              'indeterminate')
          }
          onCheckedChange={(value) => {
            return params.table.toggleAllPageRowsSelected(!!value)
          }}
          aria-label="Select all"
        />
      )

      if (visibility !== 'index') {
        return checkbox
      }

      return (
        <SelectionIndexLayer isCheckboxVisible={isCheckboxVisible}>
          <span className="text-tremor-content text-xs font-semibold">
            {renderIndex ? null : `#`}
          </span>
          {checkbox}
        </SelectionIndexLayer>
      )
    },
    id: SELECTION_COLUMN_ID,
    meta: {
      cellProps: {
        className: visibility === 'index' ? 'pr-2! pl-2!' : 'pr-2! pl-0',
        href: undefined,
      },
      headerCellProps: {
        contentClassName:
          visibility === 'index'
            ? 'pl-2 pr-2 justify-end'
            : 'pl-0 pr-2 justify-start',
      },
      isInteractive: true,
    },
    minSize: 40,
    size: 40,
  } satisfies ColumnDef<TData>
}

interface SelectionIndexLayerProps {
  children: [ReactElement, ReactElement]
  isCheckboxVisible: boolean
}

function SelectionIndexLayer({
  children: [indexNode, checkboxNode],
  isCheckboxVisible,
}: SelectionIndexLayerProps) {
  return (
    <div className="grid items-center justify-items-end">
      <div
        aria-hidden
        className={cn(
          'transition-opacity [grid-area:1/1]',
          isCheckboxVisible
            ? 'opacity-0'
            : 'opacity-100 group-focus-within/row:opacity-0 group-hover/row:opacity-0',
        )}
      >
        {indexNode}
      </div>
      <div
        className={cn(
          'flex items-center justify-end [grid-area:1/1]',
          isCheckboxVisible
            ? null
            : 'pointer-events-none group-focus-within/row:pointer-events-auto group-hover/row:pointer-events-auto',
        )}
      >
        {checkboxNode}
      </div>
    </div>
  )
}
